import pulse, { type TrackerData } from "@finn-no/pulse-sdk";
import { getCategoryForAdViewType } from "@m10s/mobility-mapping-utils";
import type { MobilityAd } from "@mobility-item-page/types";
import {
  ElementType,
  EventType,
  getSubVerticalForMobilityAd,
  Intent,
  TargetType,
} from "@mobility-item-page/utilities/client";
import { useMemo } from "react";
import { ObjectPosition } from "../../types/objectPosition.ts";
import { useValidDealerContext } from "../context/DealerProviderContext.tsx";

const trackClick = ({ intent, name, target, objectData, subVertical }: TrackerData) => {
  pulse.trackEvent({
    type: EventType.Click,
    intent,
    name,
    object: {
      ...objectData,
      type: "UIElement",
      hasExtendedProfile: false,
      listingType: "professional",
    },
    target,
    vertical: {
      name: "mobility",
      subVertical,
    },
  });
};

const trackView = ({ intent, name, target, objectData, subVertical }: TrackerData) => {
  pulse.trackEvent({
    type: EventType.View,
    intent,
    name,
    object: {
      hasExtendedProfile: false,
      listingType: "professional",
      ...objectData,
    },
    target,
    vertical: {
      name: "mobility",
      subVertical,
    },
  });
};

export const useTracking = () => {
  const {
    itemData: { similarAdsFrom, externalItemUrl },
    organisationData: { name, homePage, companyRegistration, dealerBrandingPage, phoneNumber },
    external: { adViewType },
  } = useValidDealerContext();

  const subVertical = getSubVerticalForMobilityAd(adViewType);

  return useMemo(
    () => ({
      trackContactUs: (position: ObjectPosition) =>
        trackClick({
          intent: Intent.Create,
          name: "Send message",
          objectData: { id: "M_ev01", elementType: ElementType.Button, position },
          target: {
            id: "id",
            type: TargetType.Message,
          },
          subVertical,
        }),
      trackShowPhone: (position: ObjectPosition) =>
        trackClick({
          intent: Intent.Show,
          name: "Contact seller",
          objectData: {
            id: "MO_ev58",
            elementType: ElementType.Button,
            position: position,
          },
          target: {
            id: "show-phone-numbers",
            type: TargetType.ExternalContent,
            name: "Phone",
            provider: name,
          },
          subVertical,
        }),
      trackPhone: (position: ObjectPosition, intent: Intent.Call | Intent.Click) =>
        trackClick({
          intent,
          name: "Contact seller",
          objectData: {
            id: "MO_ev59",
            elementType: ElementType.PhoneNumber,
            position: position,
          },
          target: {
            id: `tel:${phoneNumber}`,
            type: TargetType.ExternalContent,
            name: "Phone",
            provider: name,
          },
          subVertical,
        }),
      trackGoToItem: () =>
        externalItemUrl &&
        trackClick({
          intent: Intent.Open,
          name: "Homepage link clicked",
          objectData: {
            id: "MO_ev60",
            name: "Itempage",
            elementType: ElementType.Link,
            position: ObjectPosition.NonStickyCTA,
          },
          target: {
            id: externalItemUrl,
            type: TargetType.ExternalContent,
            name: "Go to webpage",
            provider: name,
          },
          subVertical,
        }),
      trackHomepage: (position: ObjectPosition) =>
        homePage?.linkUrl &&
        trackClick({
          intent: Intent.Open,
          name: "Homepage link clicked",
          objectData: { id: "MO_ev60", name: "Homepage", elementType: ElementType.Link, position },
          target: {
            id: homePage.linkUrl,
            type: TargetType.ExternalContent,
            name: "Go to webpage",
            provider: name,
          },
          subVertical,
        }),
      trackSeeOtherAdsClick: (position: ObjectPosition) =>
        similarAdsFrom?.linkUrl &&
        trackClick({
          intent: Intent.View,
          name: "More ads from this organization",
          objectData: { id: "MO_ev61", elementType: ElementType.Link, position },
          target: {
            id: similarAdsFrom.linkUrl,
            name: "Partner",
            type: TargetType.Listing,
            provider: name,
          },
          subVertical,
        }),
      trackCompanyRegistration: (position: ObjectPosition) =>
        companyRegistration?.registrationNumberLinkUrl &&
        trackClick({
          intent: Intent.Open,
          name: "Dealer registration number lookup",
          objectData: { id: "MO_ev77", elementType: ElementType.Link, position },
          target: {
            id: companyRegistration.registrationNumberLinkUrl,
            type: TargetType.ExternalContent,
            name: "Go to webpage",
            provider: companyRegistration.registrationProviderName,
          },
          subVertical,
        }),
      trackSeeLocation: (position: ObjectPosition, elementType: ElementType, linkUrl?: string): void => {
        linkUrl &&
          trackClick({
            intent: Intent.Open,
            name: "Dealer map",
            objectData: { id: "MO_ev79", elementType, position },
            ...(linkUrl && {
              target: {
                id: linkUrl,
                type: linkUrl.startsWith("https://www.google.com/maps/") ? TargetType.ExternalContent : TargetType.Page,
              },
            }),
            subVertical,
          });
      },
      trackBrandingPage: (position: ObjectPosition) =>
        dealerBrandingPage?.linkUrl &&
        trackClick({
          intent: Intent.View,
          name: "Dealer branding page",
          objectData: { id: "MO_ev80", elementType: ElementType.Link, position },
          target: {
            id: dealerBrandingPage?.linkUrl,
            type: TargetType.Page,
            provider: companyRegistration?.registrationProviderName,
          },
          subVertical,
        }),
      trackViewFullDescription: () =>
        trackClick({
          intent: Intent.View,
          name: "Full dealer description",
          objectData: { id: "MO_ev81", elementType: ElementType.Button },
          subVertical,
        }),
      trackViewOpeningHours: () =>
        trackClick({
          intent: Intent.View,
          name: "Opening hours",
          objectData: { id: "MO_ev82", elementType: ElementType.Button },
          subVertical,
        }),
      trackOpenLoginModal: () =>
        trackClick({
          intent: Intent.Create,
          name: "Login modal",
          objectData: { id: "MO_ev83", elementType: ElementType.Button },
          subVertical,
        }),
      trackCancelLoginModal: () => {
        trackClick({
          intent: Intent.Cancel,
          name: "Login modal",
          objectData: { id: "MO_ev84", elementType: ElementType.Button },
          subVertical,
        });
      },
      trackViewContactFormModal: ({
        isSuccessView,
        adViewType,
      }: {
        isSuccessView: boolean;
        adViewType?: MobilityAd["adViewType"];
      }) => {
        const category = adViewType ? getCategoryForAdViewType(adViewType) : "";
        trackView({
          intent: Intent.Show,
          name: "Send message",
          objectData: {
            id: "MO_ev93",
            name: `Contact${isSuccessView ? " Success" : ""}`,
            type: "Form",
            action: "Send message",
            stepName: isSuccessView ? "" : "Write message",
            stepNumber: isSuccessView ? "" : 1,
            category,
          },
          subVertical,
        });
      },
      trackViewEIDModal: () => {
        trackView({
          name: "eID - verification",
          objectData: {
            id: "MO_ev99",
            type: "Form",
            stepName: "Verify identity",
          },
          subVertical,
        });
      },
      trackCancelContactFormModal: () => {
        trackClick({
          intent: Intent.Cancel,
          name: "Send message",
          objectData: {
            id: "MO_ev94",
            elementType: ElementType.Button,
          },
          subVertical,
        });
      },
      trackMinimizeContactFormModal: () => {
        trackClick({
          intent: Intent.Minimize,
          name: "Send message",
          objectData: {
            id: "MO_ev94",
            elementType: ElementType.Button,
          },
          subVertical,
        });
      },
      trackSubmitContactForm: (phoneNumber?: string) => {
        trackClick({
          intent: Intent.Send,
          name: "Send message",
          objectData: {
            id: "MO_ev95",
            elementType: ElementType.Button,
            name: `Contact with${phoneNumber ? "" : "out"} phone number`,
          },
          subVertical,
        });
      },
      trackCannedMessageClick: (message: string) => {
        trackClick({
          intent: Intent.Click,
          name: "Canned message",
          objectData: {
            id: "MO_ev97",
            elementType: ElementType.Button,
            name: message,
          },
          subVertical,
        });
      },
      trackSentMessageWithCheckboxes: (
        message: boolean,
        callMeBack: boolean,
        bookATestDrive: boolean,
        offerFinance: boolean,
        tradeInCar: boolean,
      ) => {
        const objectName: string[] = [];
        message && objectName.push("writtenMessage");
        callMeBack && objectName.push("callMeBack");
        bookATestDrive && objectName.push("bookATestDrive");
        offerFinance && objectName.push("offerFinance");
        tradeInCar && objectName.push("tradeInCar");
        trackClick({
          name: "Contact Form message",
          objectData: {
            id: "MO_ev100",
            elementType: ElementType.Button,
            name: objectName.join(","),
          },
        });
      },
      trackLogin: () => {
        trackClick({
          intent: Intent.Login,
          name: "Log in",
          objectData: { id: "MO_ev86", elementType: ElementType.Button },
          subVertical,
        });
      },
      trackDigitalRetailing: () =>
        trackClick({
          intent: Intent.Open,
          name: "Digital Retailing",
          objectData: {
            name: "Entry Point",
            id: "DR_ev01",
            elementType: ElementType.Button,
          },
          target: {
            id: "reserve-car-link",
            name: "Reserve car",
            type: TargetType.Form,
          },
          subVertical,
        }),
      trackDealerRatings: (url: string, position: ObjectPosition) => {
        trackClick({
          intent: Intent.Open,
          name: "External dealer ratings",
          objectData: {
            id: "MO_ev78",
            type: "UIElement",
            elementType: "Link",
            position,
          },
          target: {
            id: url,
            type: "ExternalContent",
            name: "Go to webpage",
          },
          subVertical,
        });
      },
      trackClickMemberAssociationLink: ({
        targetTitle,
        linkUrl,
        targetType,
      }: {
        targetTitle: string;
        linkUrl: string;
        targetType: TargetType.Page | TargetType.ExternalContent;
      }) => {
        trackClick({
          intent: Intent.Open,
          name: "Read about member association",
          objectData: {
            id: "MO_ev98",
            type: "UIElement",
            elementType: ElementType.Link,
            position: ObjectPosition.InfoCard,
          },
          target: {
            id: linkUrl,
            type: targetType,
            name: targetTitle,
          },
          subVertical,
        });
      },
    }),
    [
      subVertical,
      name,
      externalItemUrl,
      homePage?.linkUrl,
      similarAdsFrom.linkUrl,
      companyRegistration?.registrationNumberLinkUrl,
      companyRegistration?.registrationProviderName,
      dealerBrandingPage?.linkUrl,
      phoneNumber,
    ],
  );
};
